import { useEffect, useMemo, useState } from 'react';
import {
  Clock,
  Package,
  CheckCircle2,
  Utensils,
  Bike,
  Home,
  Receipt,
  X,
} from 'lucide-react';
import { api } from '../../lib/api';

interface BottomOrderTrackingBarProps {
  orderId: string;
  onOpenTracking: (orderId: string) => void;
  onDismiss?: () => void;
}

interface OrderStatus {
  id: string;
  label: string;
  icon: any;
}

const STATUS_STAGE_INDEX: Record<string, number> = {
  PENDING: 0,
  CONFIRMED: 1,
  PREPARING: 2,
  READY: 3,
  OUT_FOR_DELIVERY: 4,
  DELIVERED: 5,
  COMPLETED: 5,
};

const orderStatuses: OrderStatus[] = [
  { id: 'placed', label: 'Order Placed', icon: CheckCircle2 },
  { id: 'confirmed', label: 'Confirmed', icon: Receipt },
  { id: 'preparing', label: 'Preparing', icon: Utensils },
  { id: 'ready', label: 'Ready', icon: Package },
  { id: 'outForDelivery', label: 'Out for Delivery', icon: Bike },
  { id: 'delivered', label: 'Delivered', icon: Home },
];

export function BottomOrderTrackingBar({
  orderId,
  onOpenTracking,
  onDismiss,
}: BottomOrderTrackingBarProps) {
  const [order, setOrder] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!orderId) {
      return;
    }

    let mounted = true;

    const loadOrderDetails = async () => {
      try {
        const response = await api.orders.getById(orderId);
        if (mounted) {
          setOrder(response?.data || null);
        }
      } catch {
        if (mounted) {
          setOrder(null);
        }
      }
    };

    void loadOrderDetails();
    const interval = setInterval(() => void loadOrderDetails(), 15000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [orderId]);

  const currentStatusIndex = useMemo(
    () => STATUS_STAGE_INDEX[String(order?.status || '').toUpperCase()] ?? 0,
    [order?.status]
  );

  useEffect(() => {
    if (currentStatusIndex >= 5 && onDismiss) {
      const timeout = setTimeout(() => {
        setIsVisible(false);
        onDismiss();
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [currentStatusIndex, onDismiss]);

  const getEstimatedTime = () => {
    const status = String(order?.status || '').toUpperCase();

    if (status === 'DELIVERED' || status === 'COMPLETED') return 'Delivered';
    if (status === 'OUT_FOR_DELIVERY') return 'Arriving soon';
    if (status === 'READY') return 'Dispatching soon';
    if (status === 'PREPARING') return 'Preparing';
    if (status === 'CONFIRMED') return 'Confirmed';
    return 'Updating...';
  };

  const currentStatus = orderStatuses[currentStatusIndex];

  if (!isVisible || !order) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 animate-slide-up">
      <div className="bg-gradient-to-r from-blue-50 via-white to-blue-50 border-t-2 border-blue-200 shadow-2xl backdrop-blur-sm">
        <div className="h-1 bg-gradient-to-r from-blue-500 via-green-500 to-orange-500 relative overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-400 via-green-400 to-orange-400 transition-all duration-1000 ease-out opacity-80 animate-pulse"
            style={{ width: `${((currentStatusIndex + 1) / orderStatuses.length) * 100}%` }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-blue-500 rounded-full blur-md opacity-40 animate-pulse"></div>
                <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 flex items-center justify-center shadow-lg">
                  <Package className="w-6 h-6 text-white drop-shadow-md" />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-bold text-gray-900 text-sm">
                    Order #{order.orderNumber || order.id}
                  </p>
                  {currentStatusIndex < 5 && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-400 to-green-500 text-white shadow-md animate-pulse">
                      Live
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-gray-900">{currentStatus?.label}</p>
                  <span className="text-gray-400">•</span>
                  <p className="text-sm text-gray-700 flex items-center gap-1 font-medium">
                    <Clock className="w-3.5 h-3.5 text-orange-500" />
                    {getEstimatedTime()}
                  </p>
                </div>
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-0 flex-1 max-w-2xl mx-6">
              {orderStatuses.map((status, index) => {
                const StatusIcon = status.icon;
                const isCompleted = currentStatusIndex >= index;
                const isCurrent = currentStatusIndex === index;

                return (
                  <div key={status.id} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="relative">
                        {(isCompleted || isCurrent) && (
                          <div className={`absolute inset-0 rounded-full blur-md opacity-50 ${
                            isCurrent ? 'bg-blue-400 animate-pulse' : 'bg-green-400'
                          }`}></div>
                        )}
                        <div
                          className={`relative w-7 h-7 rounded-full flex items-center justify-center transition-all duration-500 shadow-md ${
                            isCompleted
                              ? 'bg-gradient-to-br from-green-400 to-green-600 shadow-green-200'
                              : isCurrent
                              ? 'bg-gradient-to-br from-blue-500 to-blue-700 shadow-blue-300 animate-pulse'
                              : 'bg-gray-300 shadow-gray-200'
                          }`}
                        >
                          <StatusIcon className="w-4 h-4 text-white drop-shadow" />
                        </div>
                      </div>
                      <p
                        className={`text-xs mt-1.5 font-bold whitespace-nowrap ${
                          isCompleted || isCurrent ? 'text-gray-900' : 'text-gray-500'
                        }`}
                      >
                        {status.label}
                      </p>
                    </div>
                    {index < orderStatuses.length - 1 && (
                      <div className="relative h-0.5 flex-1 mx-2 bg-gray-300 rounded-full overflow-hidden">
                        <div
                          className={`absolute inset-0 transition-all duration-500 rounded-full ${
                            currentStatusIndex > index
                              ? 'bg-gradient-to-r from-green-400 to-green-600'
                              : 'bg-transparent'
                          }`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => onOpenTracking(orderId)}
                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm"
              >
                Track Order
              </button>
              {onDismiss && (
                <button
                  onClick={() => {
                    setIsVisible(false);
                    onDismiss();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
